import { useState, useEffect } from "react";
import { Container, Box, Card, CardContent, Grid, Chip, Skeleton, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, } from "@mui/material";
import { MdWifi, MdWifiOff, MdCheckCircleOutline, MdOutlineCancel, MdHourglassEmpty, MdCircle, MdRestartAlt, MdPowerSettingsNew, MdMemory } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import { differenceInMinutes, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale'; // para mostrar en español

function getClosestTimestamp(obj) {
    const now = Date.now();
    let closestKey = null;
    let closestValue = null;
    let minDiff = Infinity;
    for (const key in obj) {
        if (obj[key] && typeof obj[key].ts === 'number') {
            const diff = Math.abs(now - obj[key].ts);
            if (diff < minDiff) {
                minDiff = diff;
                closestKey = key;
                closestValue = obj[key];
            }
        }
    }
    return {
        key: closestKey,
        ts: closestValue?.ts ?? null,
        last_message: closestValue?.last_message ?? null,
        diffMs: minDiff,
    };
}


const statusConfig = {
    true: { label: "Actualizado", bg: "#dcfce7", color: "#166534", icon: <MdCheckCircleOutline color="#22c55e" />, },
    false: { label: "Sin Respuesta", bg: "#fee2e2", color: "#991b1b", icon: <MdOutlineCancel color="#ef4444" />, },
    pending: { label: "Pendiente", bg: "#ffedd5", color: "#9a3412", icon: <MdHourglassEmpty color="#f97316" className="animate-spin" />, },
};

const StatusChip = ({ rs485, timeTrigger }) => {
    const closest = getClosestTimestamp(rs485);
    const diff = differenceInMinutes(new Date(), new Date(closest.ts));
    const config = statusConfig[closest.last_message ? "true" : (diff >= process.env.REACT_APP_TIME_LIMIT) ? "false" : "pending"];
    return (
        <Chip
            className="w-fit !px-1"
            size="small"
            label={config.label}
            sx={{
                fontWeight: 600,
                backgroundColor: config.bg,
                color: config.color,
                fontSize: 14,
            }}
            icon={config.icon}
        />
    );
};

export default function PanelStatusBody({ data = [], onReprobe, onResetMaster, onResetRouter }) {
    const navigate = useNavigate();

    const [timeTrigger, setTimeTrigger] = useState(0); // usado para forzar render
    const [confirmReset, setConfirmReset] = useState(null);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimeTrigger(prev => prev + 1);
        }, 30000);

        return () => clearInterval(interval);
    }, []);
    // const handleReprobe = (masterId) => {
    //     if (typeof onReprobe === "function") {
    //         onReprobe(masterId);
    //     } else {
    //         console.log("Reprobe ->", masterId);
    //     }
    // };
    if (!data || data.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box>
                    <Skeleton variant="text" width={200} height={40} />
                    <Skeleton variant="rectangular" height={100} sx={{ my: 2, borderRadius: 2 }} />
                    <Grid container spacing={2}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Grid item xs={12} sm={6} lg={4} key={i}>
                                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        );
    }

    const formatResetTime = (timestamp) => {
        if (!timestamp) return "Nunca";

        // Si Firebase lo guarda en segundos:
        const timeMs = timestamp < 1000000000000
            ? timestamp * 1000
            : timestamp;

        return formatDistanceToNow(
            new Date(timeMs),
            {
                addSuffix: true,
                locale: es
            }
        );
    };

    const getResetStatus = (status) => {
        switch (status) {
            case 0: return { label: "Disponible", bg: "#f1f5f9", color: "#475569" };
            case 1: return { label: "Procesando", bg: "#ffedd5", color: "#9a3412" };
            case 2: return { label: "Completado", bg: "#dcfce7", color: "#166534" };
            default: return { label: `Estado ${status ?? "N/A"}`, bg: "#f1f5f9", color: "#475569" };
        }
    };

    const handleConfirmReset = async () => {
        try {
            if (confirmReset === "master") {
                console.log("Reinicio de maestro1")
                if (typeof onResetMaster === "function") {
                    console.log("Reinicio de maestro2")
                    await onResetMaster();
                }
            }
            if (confirmReset === "router") {
                if (typeof onResetRouter === "function") {
                    console.log("Reinicio electrico de todo")
                    await onResetRouter();
                }
            }
        } catch (error) {
            console.error("Error ejecutando reinicio:", error);
        } finally {
            setConfirmReset(null);
        }
    };

    // console.warn(data)
    return (
        <div className="w-full h-full px-4 max-[900px]:px-0">
            <div className="w-full" >
                <div className="mb-4">
                    <h1 className="w-full text-4xl font-bold mb-2" >Estado de Panel</h1>
                    <p >Monitoreo en tiempo real de la conexión del panel de voladura y confirmación de cambio de texto en los campos visuales.</p>
                </div>
                {/* Selección de Master */}
                <section className="mb-6">
                    <h2 className="text-2xl mb-3 font-semibold">
                        Seleccionar Panel de Voladura
                    </h2>
                    <div className="flex flex-wrap gap-4 max-[600px]:flex-col">
                        {data.Masters.map((master, index) => {
                            const active = index === data.IndexData;
                            return (
                                <div key={index} className="flex-auto" >
                                    <label
                                        className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer bg-white ${active
                                            ? "border-primary shadow-lg"
                                            : "border-gray-300 hover:border-[#2e354d]/50"
                                            }`}
                                        onClick={() =>
                                            navigate(`/Dashboard/Conexion/${master.Path}`)}
                                    >
                                        <input
                                            type="radio"
                                            name="master-panel"
                                            value={master.Path}
                                            checked={active}
                                            onChange={() => navigate(`/Dashboard/Conexion/${master.Path}`)}
                                            className="form-radio text-[#2e354d] focus:ring-[#2e354d]"
                                        />
                                        <span className="font-medium text-gray-900">{master.Name}</span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <AnimatePresence mode="wait">
                    {data ? (
                        <motion.div
                            key={data.connection.ID}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -50 }}
                            transition={{ duration: 0.2 }}
                        >
                            <section className="mb-6">
                                <h2 className="text-2xl mb-3 font-semibold">
                                    Estado de conexión del Panel
                                </h2>

                                <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                                    <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, }}>
                                            <Box sx={{ bgcolor: "#2e354d11", color: data.connection.Online ? "#22c55e" : "#ef4444", p: 1.5, borderRadius: "9999px", }} >
                                                {data.connection.Online ? (<MdWifi size={26} />) : (<MdWifiOff size={26} />)}
                                            </Box>
                                            <Box>
                                                <h3 className="font-semibold text-xl">
                                                    {data.connection.Online ? "Conectado a Internet" : "Desconectado de Internet"}
                                                </h3>
                                                <h4 className="font-normal text-md" >
                                                    {data.connection.detail ?? "Panel de Voladura"}
                                                </h4>
                                            </Box>
                                        </Box>

                                        <Box>
                                            <Chip
                                                icon={<MdCircle color={`${data.connection.Online ? "#22c55e" : "#ef4444"}`} />}
                                                label={data.connection.Online ? "Online" : "Offline"}
                                                sx={{ fontWeight: 700, backgroundColor: data.connection.Online ? "#dcfce7 " : "#fee2e2 ", color: data.connection.Online ? "#166534" : "#991b1b", fontSize: 14, width: 100, letterSpacing: 0.5, }}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </section>

                            <section className="mb-6">
                                <h2 className="text-2xl mb-3 font-semibold">
                                    {`Campos - ${data.connection.name}`}
                                </h2>

                                <div className="grid grid-cols-3 gap-4 max-[1200px]:grid-cols-2 max-[760px]:grid-cols-1">
                                    {data.connection?.Slaves?.length > 0
                                        ?  [...data.connection.Slaves].map((slave, i) => (
                                            <div className="" xs={12} sm={6} lg={4} key={slave.id ?? i}>
                                                <Card sx={{ borderRadius: 2 }}>
                                                    <CardContent>
                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }} >
                                                            <h3 className="font-bold text-xl">
                                                                {slave.Name}
                                                            </h3>
                                                            <StatusChip key={slave.id ?? i} rs485={slave} timeTrigger={timeTrigger} />
                                                        </Box>
                                                        <h4 className="font-normal text-md mt-2">
                                                            Ultima actualización: {formatDistanceToNow(new Date(slave.Update.ts), { addSuffix: true, locale: es }) ?? "N/A"}
                                                        </h4>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        ))
                                        : Array.from({ length: 3 }).map((_, i) => (
                                            <Grid item xs={12} sm={6} lg={4} key={i}>
                                                <Skeleton
                                                    variant="rectangular"
                                                    height={120}
                                                    sx={{ borderRadius: 2 }}
                                                />
                                            </Grid>
                                        ))}
                                </div>
                            </section>
                            {data.Maitenance && (
                            <section className="mb-6">
                                <h2 className="text-2xl mb-3 font-semibold">
                                    Acciones de mantenimiento
                                </h2>

                                <div className="grid grid-cols-2 gap-4 max-[760px]:grid-cols-1">

                                    <Card sx={{ borderRadius: 2,boxShadow: 2 }}>
                                        <CardContent>
                                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }} >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    <Box sx={{ bgcolor: "#0f346011", color: "#0f3460", p: 1.5, borderRadius: "9999px" }} >
                                                        <MdMemory size={28} />
                                                    </Box>
                                                    <Box>
                                                        <h3 className="font-bold text-xl">
                                                            Reinicio de controlador
                                                        </h3>
                                                        <p className="text-sm text-gray-600 mt-1">
                                                            Reinicia únicamente el maestro.
                                                        </p>
                                                    </Box>
                                                </Box>

                                                <Chip size="small" label={ getResetStatus(data.resetPanel?.Master_Status).label }
                                                    sx={{
                                                        fontWeight: 600,
                                                        backgroundColor:
                                                            getResetStatus(
                                                                data.resetPanel?.Master_Status
                                                            ).bg,
                                                        color:
                                                            getResetStatus(
                                                                data.resetPanel?.Master_Status
                                                            ).color,
                                                    }}
                                                />
                                            </Box>

                                            <p className="text-sm text-gray-500 mb-4">
                                                Último reinicio:{" "}
                                                {formatResetTime(data.resetPanel?.Master_ts)}
                                            </p>

                                            <Button 
                                                fullWidth 
                                                variant="contained" 
                                                startIcon={<MdRestartAlt size={22} />} 
                                                onClick={() => setConfirmReset("master")} 
                                                disabled={data.resetPanel?.Master_Force === true || data.resetPanel?.Master_Status == 1 }
                                                sx={{
                                                    backgroundColor: "#0f3460",
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    borderRadius: 2,
                                                    py: 1.2,
                                                    "&:hover": {
                                                        backgroundColor: "#0a2540",
                                                    }
                                                }}
                                            >
                                                {data.resetPanel?.Master_Force
                                                    ? "Reinicio solicitado"
                                                    : "Reiniciar maestro"}
                                            </Button>
                                        </CardContent>
                                    </Card>

                                    <Card sx={{ borderRadius: 2, boxShadow: 2, border: "1px solid #fed7aa" }} >
                                        <CardContent>
                                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 2 }} >
                                                <Box sx={{display: "flex", alignItems: "center", gap: 2 }}>
                                                    <Box sx={{bgcolor: "#fff7ed", color: "#ea580c", p: 1.5, borderRadius: "9999px" }}>
                                                        <MdPowerSettingsNew size={28} />
                                                    </Box>
                                                    <Box>
                                                        <h3 className="font-bold text-xl">
                                                            Reinicio eléctrico
                                                        </h3>
                                                        <p className="text-sm text-gray-600 mt-1">
                                                            Corta y restablece la alimentación del panel.
                                                        </p>
                                                    </Box>
                                                </Box>

                                                <Chip
                                                    size="small"
                                                    label={
                                                        getResetStatus(
                                                            data.resetPanel?.Router_Status
                                                        ).label
                                                    }
                                                    sx={{
                                                        fontWeight: 600,
                                                        backgroundColor:
                                                            getResetStatus(
                                                                data.resetPanel?.Router_Status
                                                            ).bg,
                                                        color:
                                                            getResetStatus(
                                                                data.resetPanel?.Router_Status
                                                            ).color,
                                                    }}
                                                />
                                            </Box>

                                            <p className="text-sm text-gray-500 mb-4">
                                                Último reinicio:{" "}
                                                {formatResetTime(
                                                    data.resetPanel?.Router_ts
                                                )}
                                            </p>

                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<MdPowerSettingsNew size={22} />}
                                                onClick={() => setConfirmReset("router")}
                                                disabled={data.resetPanel?.Router_Force === true || data.resetPanel?.Router_Status === 1}
                                                sx={{
                                                    borderColor: "#ea580c",
                                                    color: "#c2410c",
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    borderRadius: 2,
                                                    py: 1.2,
                                                    "&:hover": {
                                                        borderColor: "#c2410c",
                                                        backgroundColor: "#fff7ed",
                                                    }
                                                }}
                                            >
                                                {data.resetPanel?.Router_Force
                                                    ? "Reinicio solicitado"
                                                    : "Reiniciar alimentación"}
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </div>
                            </section>
                            )}
                            

                        </motion.div>
                    ) : (
                        <Skeleton
                            variant="rectangular"
                            height={80}
                            sx={{ mb: 2, borderRadius: 2 }}
                        />
                    )}
                </AnimatePresence>
            </div>
            <Dialog open={confirmReset !== null} onClose={() => setConfirmReset(null)} >
                <DialogTitle> {confirmReset === "master" ? "Reiniciar controlador maestro" : "Reinicio eléctrico del panel"} </DialogTitle>
                <DialogContent>
                    <DialogContentText> 
                        {confirmReset === "master"
                            ? "Esta acción reiniciará el firmware del controlador maestro del panel. ¿Desea continuar?"
                            : "Esta acción cortará temporalmente la alimentación eléctrica completa del panel y posteriormente la restablecerá. ¿Desea continuar?"}
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setConfirmReset(null)} color="inherit" > Cancelar </Button>
                    <Button onClick={handleConfirmReset} variant="contained"
                        sx={{
                            backgroundColor:
                                confirmReset === "router"
                                    ? "#ea580c"
                                    : "#0f3460"
                        }}
                    >
                        {confirmReset === "master"
                            ? "Reiniciar maestro"
                            : "Reiniciar panel"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
